import React, { useState, useCallback, useEffect, useMemo } from "react";
import {
  Button,
  Group,
  Text,
  Stack,
  TextInput,
  Switch,
  ColorInput,
  Collapse,
  Box,
  Tooltip,
  Select,
  Checkbox,
  Modal,
  NumberInput,
  Loader,
} from "@mantine/core";
import ServiceDescriptionEditor from "../../common/ServiceDescriptionEditor";
import CustomStepper from "./CustomStepper";
import {
  IconCheck,
  IconPlus,
  IconEdit,
  IconTrash,
  IconBulb,
  IconChevronDown,
  IconChevronUp,
  IconChevronLeft,
  IconChevronRight,
  IconInfoCircle,
  IconX,
} from "@tabler/icons-react";
import {
  notifyError,
  notifySuccess,
  notifyWarning,
} from "../../../utils/notify";
import Logo from "../../common/Logo/Logo";
import ProgressiveWizardShell from "./ProgressiveWizardShell";
import { useApp } from "../../../context/AppContext";
import { useSalon } from "../../../context/SalonContext";
import usePermissions from "../../../hooks/usePermissions";
import { RequiredFormsSection } from "../../formTemplates/RequiredFormsSection";
import { ServiceMessageSettings } from "../../formTemplates/ServiceMessageSettings";
import { ServicePostAppointmentSection } from "../../servicePostAppointment/ServicePostAppointmentSection";
import TaxRateMultiSelect from "../../settings/TaxRateMultiSelect";
import {
  MessageSettings,
  DEFAULT_MESSAGE_SETTINGS,
  hasNonDefaultMessageSettings,
} from "../../../types/messageTemplates";
import { PendingRequiredForm } from "../../../types/formTemplates";
import {
  DEFAULT_POST_APPOINTMENT_SETTINGS,
  PostAppointmentSettings,
  PendingPostAppointmentMessage,
} from "../../../types/servicePostAppointment";
import { useServiceRequiredForms } from "../../../context/ServiceRequiredFormsContext";
import { useServicePreAppointmentDocuments } from "../../../context/ServicePreAppointmentDocumentsContext";
import { ServicePreAppointmentDocumentsSection } from "../../servicePreAppointment/ServicePreAppointmentDocumentsSection";
import { PendingPreAppointmentDocument } from "../../../types/servicePreAppointment";
import { useServicePostAppointment } from "../../../context/ServicePostAppointmentContext";
import { getApiUrl, API_CONFIG } from "../../../config/api";
import UpdateStaffServicesModal from "../../common/UpdateStaffServicesModal";
import {
  OnboardingProgressResponse,
  UpdateOnboardingProgressRequest,
} from "../types/onboarding";
import { brandColors } from "../../../theme/brandTheme";
// @ts-ignore
import { filterBookableActiveStaff } from "../../../utils/staffUtils";
import styles from "./ProgressiveServicesWizard.module.css";
import {
  StaffServiceSettingsFields,
  StaffServiceSettingsData,
  createDefaultStaffSettings,
  generateDurationOptions,
} from "../../forms/StaffServiceSettings";

type WizardStep = "intro" | "list" | "add-info" | "add-staff";

interface ServiceFormData {
  name: string;
  serviceType: string;
  description: string;
  duration: number;
  processingTime: number | string;
  finishingTime: number | string;
  bufferTime: number | string;
  enableDoubleBooking: boolean;
  price: number;
  calendarColor: string;
  showInOnlineBooking: boolean;
  faron__is_private_room: boolean;
  // Per-service Card-on-File requirement (migrated from salon-wide setting)
  require_card_to_book: boolean;
  staffIds: string[];
  staffSettings: Record<string, StaffServiceSettingsData>;
}

interface ProgressiveServicesWizardProps {
  opened: boolean;
  onClose: () => void;
  progress: OnboardingProgressResponse;
  onComplete: (
    postOnboardingProgress: (
      data: UpdateOnboardingProgressRequest,
      salonId?: string,
    ) => Promise<OnboardingProgressResponse>,
  ) => void;
  onSwitchWizard?: (wizard: "employees" | "services" | "payments") => void;
}

const EMPTY_SERVICE: ServiceFormData = {
  name: "",
  serviceType: "",
  description: "",
  duration: 60,
  processingTime: 0,
  finishingTime: 0,
  bufferTime: 0,
  enableDoubleBooking: true,
  price: 0,
  calendarColor: brandColors.teal,
  showInOnlineBooking: true,
  faron__is_private_room: false,
  require_card_to_book: false,
  staffIds: [],
  staffSettings: {},
};

const DURATION_OPTIONS = [
  { value: "15", label: "15 minutes" },
  { value: "30", label: "30 minutes" },
  { value: "45", label: "45 minutes" },
  { value: "60", label: "1 hour" },
  { value: "75", label: "1 hr 15 min" },
  { value: "90", label: "1 hr 30 min" },
  { value: "120", label: "2 hours" },
];

const SUGGESTED_SERVICES = [
  { name: "Haircut", type: "Hair", duration: 45, price: 45 },
  { name: "Color", type: "Hair", duration: 120, price: 90 },
  { name: "Manicure", type: "Nails", duration: 30, price: 25 },
  { name: "Facial", type: "Spa", duration: 60, price: 75 },
];

export default function ProgressiveServicesWizard({
  opened,
  onClose,
  progress,
  onComplete,
  onSwitchWizard,
}: ProgressiveServicesWizardProps) {
  const {
    updateOnboardingProgress,
    createServices,
    updateServices,
    deleteSalonService,
    getTaxRates,
    setSalonServiceTaxRates,
    fetchWithAuth,
  } = useApp();
  const { linkRequiredForm } = useServiceRequiredForms();
  const { linkPreAppointmentDocument } = useServicePreAppointmentDocuments();
  const {
    updateSettings: updatePostAppointmentSettings,
    createMessage: createPostAppointmentMessage,
  } = useServicePostAppointment();
  const {
    employees,
    businessInfo,
    services,
    refreshEmployees,
    refreshServices,
  } = useSalon() as {
    employees: any[];
    businessInfo: {
      id: string;
      stripe_connect_account_id?: string;
      stripe_connect_account_is_onboarded?: boolean | string;
    };
    services: any[];
    refreshEmployees: () => Promise<any>;
    refreshServices: () => Promise<any>;
  };
  const perms = usePermissions();
  // Every forms surface here gates on the /access forms permission, not vertical.
  const canViewClientForms =
    perms?.formsAndWaivers?.canViewClientForms === true;
  const canViewClientDocuments =
    perms?.salonDocuments?.canViewDocuments === true;
  const showRequiredForms = canViewClientForms && canViewClientDocuments;
  const canManageServices = perms?.settingsServices?.canManageServices === true;
  // Onboarding "which staff performs this service" step: only list staff who
  // can actually be booked. Assigning a hidden-calendar staff would land the
  // staff_services row in the DB, but they'd never be selectable on the
  // calendar / CBP, so it has no effect.
  const activeEmployees = useMemo(
    () => filterBookableActiveStaff(employees),
    [employees],
  );

  // Track if we've already fetched data for this modal session
  const hasFetchedRef = React.useRef(false);

  // Load employees and services when modal opens (only once per open)
  useEffect(() => {
    if (opened && businessInfo?.id && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      // Refresh both employees and services to ensure we have latest data
      refreshEmployees();
      refreshServices();
    }
    // Reset the ref when modal closes
    if (!opened) {
      hasFetchedRef.current = false;
    }
  }, [opened, businessInfo?.id]); // Intentionally exclude refresh functions to prevent infinite loop

  const initialStep: WizardStep =
    progress.progressiveServices === null ? "intro" : "list";
  const [currentStep, setCurrentStep] = useState<WizardStep>(initialStep);
  const [formData, setFormData] = useState<ServiceFormData>(EMPTY_SERVICE);
  const [initialFormData, setInitialFormData] =
    useState<ServiceFormData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [editingService, setEditingService] = useState<any | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [suggestionIndex, setSuggestionIndex] = useState(0);
  const [deletingServiceId, setDeletingServiceId] = useState<string | null>(
    null,
  );

  // Modal states for form steps
  const [infoModalOpened, setInfoModalOpened] = useState(false);
  const [additionalModalOpened, setAdditionalModalOpened] = useState(false);
  const [staffModalOpened, setStaffModalOpened] = useState(false);
  const [updateStaffModalOpened, setUpdateStaffModalOpened] = useState(false);
  const [changedFields, setChangedFields] = useState<string[]>([]);
  const [pendingSaveAction, setPendingSaveAction] = useState<
    (() => Promise<void>) | null
  >(null);
  // Track the selected update option for state updates
  const [selectedUpdateOption, setSelectedUpdateOption] = useState<
    "staff_without_customization" | "all_staff" | null
  >(null);
  // Track which operations succeeded for retry logic
  const [lastSaveSuccess, setLastSaveSuccess] = useState<{
    updateSucceeded: boolean;
    createSucceeded: boolean;
    staffServicesToCreate: any[];
  } | null>(null);

  // Tax rate state for additional information step
  const [selectedTaxRateIds, setSelectedTaxRateIds] = useState<number[]>([]);

  // Pending items for new-service creation (persisted after the service is saved)
  const [pendingMessageSettings, setPendingMessageSettings] =
    useState<MessageSettings>(DEFAULT_MESSAGE_SETTINGS);
  const [pendingRequiredForms, setPendingRequiredForms] = useState<
    PendingRequiredForm[]
  >([]);
  const [pendingPreAppointmentDocuments, setPendingPreAppointmentDocuments] =
    useState<PendingPreAppointmentDocument[]>([]);
  const [pendingPostAppointmentSettings, setPendingPostAppointmentSettings] =
    useState<PostAppointmentSettings>(DEFAULT_POST_APPOINTMENT_SETTINGS);
  const [pendingPostAppointmentMessages, setPendingPostAppointmentMessages] =
    useState<PendingPostAppointmentMessage[]>([]);

  // Load tax rates when editing a service
  useEffect(() => {
    if (!infoModalOpened && !additionalModalOpened) {
      setSelectedTaxRateIds([]);
      return;
    }
    if (!editingService?.id || !businessInfo?.id) {
      setSelectedTaxRateIds([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const rates = await getTaxRates(businessInfo.id);
        if (cancelled) return;
        const linked = (Array.isArray(rates) ? rates : [])
          .filter((r: any) => r.salon_service_ids?.includes(editingService.id))
          .map((r: any) => r.id);
        setSelectedTaxRateIds(linked);
      } catch (err) {
        console.error("Failed to load tax rates:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editingService?.id, businessInfo?.id, infoModalOpened, getTaxRates]);

  const handleIntroNext = useCallback(async () => {
    setIsLoading(true);
    try {
      // Only persist progress when the salon id is known: without the
      // ?salonId= query param the backend silently falls back to a
      // staff-keyed row that can belong to another location.
      if (businessInfo?.id) {
        await updateOnboardingProgress(
          {
            section: "progressive_services",
            state: "inprogress",
          },
          businessInfo.id,
        );
      }
      setCurrentStep("list");
    } catch (error) {
      notifyError("Failed to update progress. Please try again.", {
        title: "Error",
        icon: <IconX size={18} />,
        withCloseButton: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [updateOnboardingProgress, businessInfo?.id]);

  const handleAddService = (prefill?: Partial<ServiceFormData>) => {
    setFormData({ ...EMPTY_SERVICE, ...prefill });
    setEditingService(null);
    setLastSaveSuccess(null);
    setPendingMessageSettings(DEFAULT_MESSAGE_SETTINGS);
    setPendingRequiredForms([]);
    setPendingPreAppointmentDocuments([]);
    setPendingPostAppointmentSettings(DEFAULT_POST_APPOINTMENT_SETTINGS);
    setPendingPostAppointmentMessages([]);
    setInfoModalOpened(true);
  };

  const handleEditService = (service: any) => {
    // Find the latest service data from the refreshed services array
    // This ensures we have the most up-to-date data including newly added staff members
    const latestService =
      services?.find((s: any) => s.id === service.id) || service;

    // Reset save success state when starting a new edit
    setLastSaveSuccess(null);

    // Clear any new-service pending message-settings so they don't leak across
    // an add→edit transition (the existing-service branch of
    // ServiceMessageSettings ignores pendingSettings, but resetting here keeps
    // dirty-checks and any future shared state consistent).
    setPendingMessageSettings(DEFAULT_MESSAGE_SETTINGS);
    setPendingPostAppointmentSettings(DEFAULT_POST_APPOINTMENT_SETTINGS);
    setPendingPostAppointmentMessages([]);

    // Build staffSettings from existing servicing_staff data
    const staffSettings: Record<string, StaffServiceSettingsData> = {};
    const staffIds: string[] = [];

    if (
      latestService.servicing_staff &&
      Array.isArray(latestService.servicing_staff)
    ) {
      latestService.servicing_staff.forEach((s: any) => {
        staffIds.push(s.staff_id);
        staffSettings[s.staff_id] = {
          id: s.id || null,
          enabled: true,
          isOpen: false,
          price: s.price ?? latestService.default_price ?? 0,
          duration: String(
            s.base_duration_mins ??
              s.duration_mins ??
              latestService.default_base_duration_mins ??
              latestService.default_duration_mins ??
              60,
          ),
          processingTime: String(
            s.processing_time_mins ??
              latestService.default_processing_time_mins ??
              0,
          ),
          finishingTime: String(
            s.finishing_time_mins ??
              latestService.default_finishing_time_mins ??
              0,
          ),
          bufferTime: String(
            s.buffer_time_mins ?? latestService.default_buffer_time_mins ?? 0,
          ),
          startsAt: s.is_variable_price ?? false,
          is_self_servicable:
            s.is_self_servicable ??
            latestService.default_is_self_servicable !== false,
        };
      });
    }

    const initialData: ServiceFormData = {
      name: latestService.name || "",
      serviceType: latestService.service_type || "",
      description: latestService.description || "",
      duration:
        latestService.default_base_duration_mins ??
        latestService.default_duration_mins ??
        60,
      processingTime: latestService.default_processing_time_mins ?? 0,
      finishingTime: latestService.default_finishing_time_mins ?? 0,
      bufferTime: latestService.default_buffer_time_mins ?? 0,
      enableDoubleBooking: latestService.enable_double_booking ?? true,
      price: latestService.default_price || 0,
      calendarColor:
        latestService.default_calendar_event_color || brandColors.teal,
      showInOnlineBooking: latestService.default_is_self_servicable !== false,
      faron__is_private_room: latestService.faron__is_private_room || false,
      require_card_to_book: latestService.require_card_to_book ?? false,
      staffIds,
      staffSettings,
    };

    setFormData(initialData);
    setInitialFormData(initialData);
    setEditingService(latestService);
    setInfoModalOpened(true);
  };

  const handleDeleteService = useCallback(
    async (service: any) => {
      setDeletingServiceId(service.id);
      try {
        await deleteSalonService(service.id);
        // Fetch fresh services list using SalonContext's refreshServices
        await refreshServices();
        notifySuccess(`${service.name} has been removed.`, {
          title: "Deleted",
          icon: <IconCheck size={18} />,
          withCloseButton: true,
        });
      } catch (error) {
        notifyError("Failed to delete service.", {
          title: "Error",
          icon: <IconX size={18} />,
          withCloseButton: true,
        });
      } finally {
        setDeletingServiceId(null);
      }
    },
    [deleteSalonService, refreshServices],
  );

  const handleBackToList = () => {
    setFormData(EMPTY_SERVICE);
    setEditingService(null);
    setLastSaveSuccess(null);
    setSelectedTaxRateIds([]);
    setPendingMessageSettings(DEFAULT_MESSAGE_SETTINGS);
    setPendingRequiredForms([]);
    setPendingPreAppointmentDocuments([]);
    setCurrentStep("list");
    setInfoModalOpened(false);
    setAdditionalModalOpened(false);
    setStaffModalOpened(false);
  };

  // Matches bookings-infra validations.py caps and the ServiceFormWizard Settings flow.
  const infoFieldsInvalid =
    !formData.name?.trim() ||
    formData.name.length > 256 ||
    !formData.serviceType?.trim() ||
    formData.serviceType.length > 128 ||
    (formData.description && formData.description.length > 8000) ||
    !formData.duration ||
    formData.duration <= 0 ||
    !formData.price ||
    formData.price <= 0;

  // Modal handlers
  const handleInfoNext = () => {
    if (infoFieldsInvalid) {
      return;
    }

    // If editing and default fields changed, show update staff services modal
    if (editingService && initialFormData) {
      const changed = getChangedDefaultFields(initialFormData, formData);
      if (changed.length > 0) {
        setChangedFields(changed);
        setUpdateStaffModalOpened(true);
        return;
      }
    }

    setInfoModalOpened(false);
    setAdditionalModalOpened(true);
  };

  const handleInfoModalClose = () => {
    setInfoModalOpened(false);
    setFormData(EMPTY_SERVICE);
    setInitialFormData(null);
    setEditingService(null);
    setLastSaveSuccess(null);
    setSelectedTaxRateIds([]);
    setPendingMessageSettings(DEFAULT_MESSAGE_SETTINGS);
    setPendingRequiredForms([]);
    setPendingPreAppointmentDocuments([]);
  };

  const handleAdditionalNext = () => {
    setAdditionalModalOpened(false);
    setStaffModalOpened(true);
  };

  const handleAdditionalBack = () => {
    setAdditionalModalOpened(false);
    setInfoModalOpened(true);
  };

  const handleAdditionalModalClose = () => {
    setAdditionalModalOpened(false);
    setFormData(EMPTY_SERVICE);
    setInitialFormData(null);
    setEditingService(null);
    setLastSaveSuccess(null);
    setSelectedTaxRateIds([]);
    setPendingMessageSettings(DEFAULT_MESSAGE_SETTINGS);
    setPendingRequiredForms([]);
    setPendingPreAppointmentDocuments([]);
  };

  const handleStaffBack = () => {
    setStaffModalOpened(false);
    setAdditionalModalOpened(true);
  };

  const handleStaffModalClose = () => {
    setStaffModalOpened(false);
    setFormData(EMPTY_SERVICE);
    setInitialFormData(null);
    setEditingService(null);
    setLastSaveSuccess(null);
    setSelectedTaxRateIds([]);
    setPendingMessageSettings(DEFAULT_MESSAGE_SETTINGS);
    setPendingRequiredForms([]);
    setPendingPreAppointmentDocuments([]);
  };

  // Helper function to get changed default fields
  const getChangedDefaultFields = (
    initial: ServiceFormData | null,
    current: ServiceFormData,
  ): string[] => {
    if (!initial) return [];

    const changedFields: string[] = [];

    if (initial.duration !== current.duration) {
      changedFields.push("Base Duration");
    }
    if (
      Number(initial.processingTime ?? 0) !==
      Number(current.processingTime ?? 0)
    ) {
      changedFields.push("Processing Time");
    }
    if (
      Number(initial.finishingTime ?? 0) !== Number(current.finishingTime ?? 0)
    ) {
      changedFields.push("Finishing Time");
    }
    if (Number(initial.bufferTime ?? 0) !== Number(current.bufferTime ?? 0)) {
      changedFields.push("Buffer Time");
    }
    if (initial.price !== current.price) {
      changedFields.push("Price");
    }
    if (initial.showInOnlineBooking !== current.showInOnlineBooking) {
      changedFields.push("Show in online booking");
    }
    if (initial.faron__is_private_room !== current.faron__is_private_room) {
      changedFields.push("Private Room");
    }

    return changedFields;
  };

  // Helper function to check if staff service has customization
  const hasStaffCustomization = (
    staffService: StaffServiceSettingsData,
    serviceDefaults: ServiceFormData,
  ): boolean => {
    const staffPrice = staffService.price;
    const staffDuration = parseInt(staffService.duration || "0");
    const staffProcessingTime = parseInt(staffService.processingTime ?? "0");
    const staffFinishingTime = parseInt(staffService.finishingTime ?? "0");
    const staffBufferTime = parseInt(staffService.bufferTime ?? "0");
    const staffIsSelfServicable = staffService.is_self_servicable;

    const defaultPrice = serviceDefaults.price;
    const defaultDuration = serviceDefaults.duration;
    const defaultProcessingTime = Number(serviceDefaults.processingTime ?? 0);
    const defaultFinishingTime = Number(serviceDefaults.finishingTime ?? 0);
    const defaultBufferTime = Number(serviceDefaults.bufferTime ?? 0);
    const defaultIsSelfServicable = serviceDefaults.showInOnlineBooking;

    return (
      staffPrice !== defaultPrice ||
      staffDuration !== defaultDuration ||
      staffProcessingTime !== defaultProcessingTime ||
      staffFinishingTime !== defaultFinishingTime ||
      staffBufferTime !== defaultBufferTime ||
      staffIsSelfServicable !== defaultIsSelfServicable
    );
  };

  const performSave = useCallback(
    async (updateOption?: "staff_without_customization" | "all_staff") => {
      setIsLoading(true);
      try {
        if (editingService) {
          // Update existing service
          let staffServicesToUpdate: any[] = [];
          let staffServicesToCreate: any[] = [];

          if (updateOption === "staff_without_customization") {
            // "Staff without customization" means:
            // - Update ONLY the service defaults (default_price, default_duration, etc.)
            // - Do NOT update any existing staff services in the backend
            // - DO create new staff services (staff added in this session)
            // - New staff added later will automatically get the new defaults
            staffServicesToUpdate = []; // Don't update existing staff services

            // But DO create new staff services (staff in formData but not in servicing_staff)
            formData.staffIds.forEach((staffId) => {
              const staffService = editingService.servicing_staff?.find(
                (s: any) => s.staff_id === staffId,
              );
              const staffSettings = formData.staffSettings[staffId];

              // Only create if this is a new staff service (not in servicing_staff)
              if (!staffService) {
                staffServicesToCreate.push({
                  salon_service_id: editingService.id,
                  staff_id: staffId,
                  price: formData.price, // Use new default
                  base_duration_mins: formData.duration, // Use new default
                  processing_time_mins: Number(formData.processingTime) || 0, // Use new default
                  finishing_time_mins: Number(formData.finishingTime) || 0, // Use new default
                  buffer_time_mins: Number(formData.bufferTime) || 0, // Use new default
                  is_variable_price: staffSettings?.startsAt ?? false,
                  is_self_servicable: formData.showInOnlineBooking, // Use new default
                });
              }
              // Existing staff services are not updated (keeps their current values)
            });
          } else if (updateOption === "all_staff") {
            // Update all enabled staff services and create new ones
            formData.staffIds.forEach((staffId) => {
              const staffService = editingService.servicing_staff?.find(
                (s: any) => s.staff_id === staffId,
              );
              const staffSettings = formData.staffSettings[staffId];

              if (staffService) {
                // Existing staff service - update with new defaults
                staffServicesToUpdate.push({
                  id: staffService.id,
                  price: formData.price, // Use new default
                  base_duration_mins: formData.duration, // Use new default
                  processing_time_mins: Number(formData.processingTime) || 0, // Use new default
                  finishing_time_mins: Number(formData.finishingTime) || 0, // Use new default
                  buffer_time_mins: Number(formData.bufferTime) || 0, // Use new default
                  is_variable_price: staffSettings?.startsAt ?? false, // Keep existing
                  is_self_servicable: formData.showInOnlineBooking, // Use new default
                });
              } else {
                // New staff service - create with default values
                staffServicesToCreate.push({
                  salon_service_id: editingService.id,
                  staff_id: staffId,
                  price: formData.price,
                  base_duration_mins: formData.duration,
                  processing_time_mins: Number(formData.processingTime) || 0,
                  finishing_time_mins: Number(formData.finishingTime) || 0,
                  buffer_time_mins: Number(formData.bufferTime) || 0,
                  is_variable_price: staffSettings?.startsAt ?? false,
                  is_self_servicable: formData.showInOnlineBooking,
                });
              }
            });
          } else {
            // Default: update existing staff services and create new ones
            formData.staffIds.forEach((staffId) => {
              const staffService = editingService.servicing_staff?.find(
                (s: any) => s.staff_id === staffId,
              );
              const staffSettings = formData.staffSettings[staffId];

              if (staffService) {
                // Existing staff service - update with current settings
                staffServicesToUpdate.push({
                  id: staffService.id,
                  price: staffSettings?.price ?? formData.price,
                  base_duration_mins: parseInt(
                    staffSettings?.duration ?? String(formData.duration),
                  ),
                  processing_time_mins: parseInt(
                    staffSettings?.processingTime ??
                      String(formData.processingTime ?? "0"),
                  ),
                  finishing_time_mins: parseInt(
                    staffSettings?.finishingTime ??
                      String(formData.finishingTime ?? "0"),
                  ),
                  buffer_time_mins: parseInt(
                    staffSettings?.bufferTime ??
                      String(formData.bufferTime ?? "0"),
                  ),
                  is_variable_price: staffSettings?.startsAt ?? false,
                  is_self_servicable:
                    staffSettings?.is_self_servicable ??
                    formData.showInOnlineBooking,
                });
              } else {
                // New staff service - create with default values from formData
                staffServicesToCreate.push({
                  salon_service_id: editingService.id,
                  staff_id: staffId,
                  price: staffSettings?.price ?? formData.price,
                  base_duration_mins: parseInt(
                    staffSettings?.duration ?? String(formData.duration),
                  ),
                  processing_time_mins: parseInt(
                    staffSettings?.processingTime ??
                      String(formData.processingTime ?? "0"),
                  ),
                  finishing_time_mins: parseInt(
                    staffSettings?.finishingTime ??
                      String(formData.finishingTime ?? "0"),
                  ),
                  buffer_time_mins: parseInt(
                    staffSettings?.bufferTime ??
                      String(formData.bufferTime ?? "0"),
                  ),
                  is_variable_price: staffSettings?.startsAt ?? false,
                  is_self_servicable:
                    staffSettings?.is_self_servicable ??
                    formData.showInOnlineBooking,
                });
              }
            });
          }

          // Check if this is a retry - use previous success state if available
          const previousUpdateSucceeded =
            lastSaveSuccess?.updateSucceeded ?? false;
          const previousCreateSucceeded =
            lastSaveSuccess?.createSucceeded ?? false;

          // Track which operations succeeded/failed in this attempt
          let updateSucceeded = previousUpdateSucceeded;
          let createSucceeded = previousCreateSucceeded;

          // Step 1: Update the service (only if it didn't succeed previously)
          if (!previousUpdateSucceeded) {
            const updatePayload = {
              salon_services: [
                {
                  id: editingService.id,
                  name: formData.name,
                  service_type: formData.serviceType,
                  description: formData.description,
                  default_base_duration_mins: formData.duration,
                  default_processing_time_mins:
                    Number(formData.processingTime) || 0,
                  default_finishing_time_mins:
                    Number(formData.finishingTime) || 0,
                  default_buffer_time_mins: Number(formData.bufferTime) || 0,
                  enable_double_booking: formData.enableDoubleBooking ?? true,
                  default_price: formData.price,
                  default_is_variable_price: false,
                  default_calendar_event_color: formData.calendarColor,
                  default_is_self_servicable: formData.showInOnlineBooking,
                  faron__is_private_room:
                    formData.faron__is_private_room || false,
                  require_card_to_book: formData.require_card_to_book ?? false,
                },
              ],
              staff_services: staffServicesToUpdate,
            };

            try {
              await updateServices(updatePayload);
              updateSucceeded = true;
            } catch (error) {
              notifyError("Failed to update service. Please try again.", {
                title: "Error",
                icon: <IconX size={18} />,
                withCloseButton: true,
              });
            }
          }

          // Step 2: Create new staff services (only if it didn't succeed previously)
          if (!previousCreateSucceeded && staffServicesToCreate.length > 0) {
            try {
              await createServices({
                salon_id: businessInfo?.id,
                staff_services: staffServicesToCreate,
              });
              createSucceeded = true;
            } catch (error) {
              notifyError("Failed to add staff members. Please try again.", {
                title: "Error",
                icon: <IconX size={18} />,
                withCloseButton: true,
              });
            }
          } else if (staffServicesToCreate.length === 0) {
            // No new staff services to create, so mark as succeeded
            createSucceeded = true;
          }

          // Store success state for potential retry
          setLastSaveSuccess({
            updateSucceeded,
            createSucceeded,
            staffServicesToCreate,
          });

          // Only proceed if both operations succeeded
          if (updateSucceeded && createSucceeded) {
            // Reset success state since we're completing
            setLastSaveSuccess(null);

            // Fetch fresh services list and employees
            await Promise.all([refreshServices(), refreshEmployees()]);
            notifySuccess(`${formData.name} has been updated!`, {
              title: "Success",
              icon: <IconCheck size={18} />,
              withCloseButton: true,
            });
          } else {
            // One or both failed - don't close modal, allow user to retry
            // The error notifications have already been shown above
            setIsLoading(false);
            return; // Exit early, keep modal open for retry
          }
        } else {
          // Create new service
          const servicePayload = {
            salon_id: businessInfo?.id,
            services: [
              {
                name: formData.name,
                service_type: formData.serviceType,
                description: formData.description,
                default_base_duration_mins: formData.duration,
                default_processing_time_mins:
                  Number(formData.processingTime) || 0,
                default_finishing_time_mins:
                  Number(formData.finishingTime) || 0,
                default_buffer_time_mins: Number(formData.bufferTime) || 0,
                enable_double_booking: formData.enableDoubleBooking ?? true,
                default_price: formData.price,
                default_is_variable_price: false,
                default_calendar_event_color: formData.calendarColor,
                default_is_self_servicable: formData.showInOnlineBooking,
                faron__is_private_room:
                  formData.faron__is_private_room || false,
                require_card_to_book: formData.require_card_to_book ?? false,
              },
            ],
          };

          const serviceResponse = await createServices(servicePayload);

          if (!serviceResponse || !serviceResponse[0]?.id) {
            throw new Error("Failed to create service");
          }

          const salonServiceId = serviceResponse[0].id;

          // Step 2: If staff are selected, create staff_services separately
          if (formData.staffIds && formData.staffIds.length > 0) {
            const staffServicesPayload = {
              salon_id: businessInfo?.id,
              staff_services: formData.staffIds.map((staffId) => {
                const staffSettings = formData.staffSettings[staffId];
                return {
                  salon_service_id: salonServiceId,
                  staff_id: staffId,
                  price: staffSettings?.price ?? formData.price,
                  base_duration_mins: parseInt(
                    staffSettings?.duration ?? String(formData.duration),
                  ),
                  processing_time_mins: parseInt(
                    staffSettings?.processingTime ??
                      String(formData.processingTime ?? "0"),
                  ),
                  finishing_time_mins: parseInt(
                    staffSettings?.finishingTime ??
                      String(formData.finishingTime ?? "0"),
                  ),
                  buffer_time_mins: parseInt(
                    staffSettings?.bufferTime ??
                      String(formData.bufferTime ?? "0"),
                  ),
                  is_variable_price: staffSettings?.startsAt ?? false,
                  is_self_servicable:
                    staffSettings?.is_self_servicable ??
                    formData.showInOnlineBooking,
                };
              }),
            };

            await createServices(staffServicesPayload);
          }

          // Persist message toggles only when the user changed them off the
          // all-on defaults (the backend defaults all-on anyway).
          if (hasNonDefaultMessageSettings(pendingMessageSettings)) {
            try {
              const url = getApiUrl(
                API_CONFIG.ENDPOINTS.SERVICE_MESSAGE_SETTINGS,
                "",
                { salonServiceUuid: salonServiceId },
              );
              await fetchWithAuth(url, {
                method: "PUT",
                body: JSON.stringify(pendingMessageSettings),
              });
            } catch (err) {
              console.error("Failed to save message settings:", err);
              notifyError(
                "Your message toggles were not saved. You can update them from the service editor.",
                {
                  title: "Could not save message settings",
                  icon: <IconX size={18} />,
                  withCloseButton: true,
                },
              );
            }
          }

          // Persist pending required forms
          for (const pf of pendingRequiredForms) {
            try {
              await linkRequiredForm(salonServiceId, {
                source_type: pf.source_type,
                source_id: pf.source_id,
              });
            } catch (err) {
              console.error("Failed to link required form:", err);
              notifyError(
                `"${pf.name}" was not saved. You can add it from the service editor.`,
                {
                  title: "Could not add required form",
                  icon: <IconX size={18} />,
                  withCloseButton: true,
                },
              );
            }
          }

          // Persist pending pre-appointment documents
          for (const pd of pendingPreAppointmentDocuments) {
            try {
              await linkPreAppointmentDocument(salonServiceId, {
                source_type: pd.source_type,
                source_id: pd.source_id,
              });
            } catch (err) {
              console.error("Failed to attach pre-appointment document:", err);
              notifyError(
                `"${pd.name}" was not saved. You can attach it from the service editor.`,
                {
                  title: "Could not attach document",
                  icon: <IconX size={18} />,
                  withCloseButton: true,
                },
              );
            }
          }

          const postAppointmentDirty =
            pendingPostAppointmentSettings.master_enabled;
          if (postAppointmentDirty) {
            try {
              await updatePostAppointmentSettings(salonServiceId, {
                master_enabled: pendingPostAppointmentSettings.master_enabled,
              });
            } catch (err) {
              console.error("Failed to save post-appointment settings:", err);
              notifyError(
                "Post-appointment message settings were not saved. You can configure them from the service editor.",
                {
                  title: "Could not save post-appointment messages",
                  icon: <IconX size={18} />,
                  withCloseButton: true,
                },
              );
            }
          }
          for (const msg of pendingPostAppointmentMessages) {
            try {
              await createPostAppointmentMessage(salonServiceId, {
                offset_value: msg.offset_value,
                offset_unit: msg.offset_unit,
                label: msg.label,
                attachments: msg.attachments.map((a, i) => ({
                  source_type: a.source_type,
                  source_id: a.source_id,
                  sort_order: i,
                })),
              });
            } catch (err) {
              console.error("Failed to create post-appointment message:", err);
              notifyError(
                "One of the new post-appointment messages was not saved. You can add it from the service editor.",
                {
                  title: "Could not add post-appointment message",
                  icon: <IconX size={18} />,
                  withCloseButton: true,
                },
              );
            }
          }

          // Fetch fresh services list and employees
          await Promise.all([refreshServices(), refreshEmployees()]);
          notifySuccess(`${formData.name} has been added!`, {
            title: "Success",
            icon: <IconCheck size={18} />,
            withCloseButton: true,
          });
        }

        // Persist tax rate junctions
        const serviceId = editingService?.id || null;
        if (serviceId && businessInfo?.id && selectedTaxRateIds.length >= 0) {
          try {
            await setSalonServiceTaxRates(
              businessInfo.id,
              serviceId,
              selectedTaxRateIds,
            );
          } catch (taxErr) {
            console.error(
              "Service saved but failed to update tax rates:",
              taxErr,
            );
          }
        }

        // Close the nested modals and reset form, but stay in the main services modal
        setStaffModalOpened(false);
        setAdditionalModalOpened(false);
        setInfoModalOpened(false);
        setFormData(EMPTY_SERVICE);
        setInitialFormData(null);
        setEditingService(null);
        setSelectedTaxRateIds([]);
        setPendingMessageSettings(DEFAULT_MESSAGE_SETTINGS);
        setPendingRequiredForms([]);
        setPendingPreAppointmentDocuments([]);
        setPendingPostAppointmentSettings(DEFAULT_POST_APPOINTMENT_SETTINGS);
        setPendingPostAppointmentMessages([]);
        setCurrentStep("list");
      } catch (error) {
        notifyError("Failed to save service. Please try again.", {
          title: "Error",
          icon: <IconX size={18} />,
          withCloseButton: true,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [
      formData,
      editingService,
      initialFormData,
      businessInfo?.id,
      createServices,
      updateServices,
      refreshServices,
      refreshEmployees,
      hasStaffCustomization,
      selectedTaxRateIds,
      setSalonServiceTaxRates,
      pendingMessageSettings,
      pendingRequiredForms,
      pendingPreAppointmentDocuments,
      pendingPostAppointmentSettings,
      pendingPostAppointmentMessages,
      updatePostAppointmentSettings,
      createPostAppointmentMessage,
      fetchWithAuth,
      linkRequiredForm,
      linkPreAppointmentDocument,
    ],
  );

  const handleSaveService = useCallback(async () => {
    // Use the selected update option if it was set from the modal
    // Otherwise, use default behavior (no update option)
    const updateOption = selectedUpdateOption || undefined;

    // Proceed with save (the update option is already applied to state)
    await performSave(updateOption);

    // Reset the selected option after save
    setSelectedUpdateOption(null);
  }, [selectedUpdateOption, performSave]);

  // Function to update staff settings based on selected option
  const updateStaffSettingsFromOption = useCallback(
    (option: "staff_without_customization" | "all_staff") => {
      if (!editingService) return;

      const updatedStaffSettings = { ...formData.staffSettings };

      if (option === "all_staff") {
        // Update all enabled staff services with new defaults
        formData.staffIds.forEach((staffId) => {
          const existingSettings =
            updatedStaffSettings[staffId] ||
            createDefaultStaffSettings({
              price: formData.price,
              duration: formData.duration,
              showInOnlineBooking: formData.showInOnlineBooking,
              processingTime: formData.processingTime,
              finishingTime: formData.finishingTime,
              bufferTime: formData.bufferTime,
            });
          updatedStaffSettings[staffId] = {
            ...existingSettings,
            price: formData.price,
            duration: String(formData.duration),
            processingTime: String(formData.processingTime ?? "0"),
            finishingTime: String(formData.finishingTime ?? "0"),
            bufferTime: String(formData.bufferTime ?? "0"),
            is_self_servicable: formData.showInOnlineBooking,
            // Keep existing customizations like startsAt
            startsAt: existingSettings.startsAt ?? false,
            // Preserve enabled and isOpen state
            enabled: existingSettings.enabled ?? true,
            isOpen: existingSettings.isOpen ?? false,
          };
        });
      } else if (option === "staff_without_customization") {
        // "Staff without customization" means:
        // - We update the UI state ONLY for new staff (not yet in servicing_staff)
        //   so they see the new defaults when added
        // - We do NOT modify state for existing staff - they keep their current values
        // - In performSave, we will NOT update any existing staff services in the backend
        // - Only the service defaults will be updated in the backend
        formData.staffIds.forEach((staffId) => {
          const staffService = editingService.servicing_staff?.find(
            (s: any) => s.staff_id === staffId,
          );

          // Only update state for new staff (not in servicing_staff yet)
          // Existing staff keep their current settings unchanged in state
          if (!staffService) {
            // New staff - set to new defaults for UI
            const defaultSettings = createDefaultStaffSettings({
              price: formData.price,
              duration: formData.duration,
              showInOnlineBooking: formData.showInOnlineBooking,
              processingTime: formData.processingTime,
              finishingTime: formData.finishingTime,
              bufferTime: formData.bufferTime,
            });
            updatedStaffSettings[staffId] = defaultSettings;
          }
          // For existing staff, don't modify their settings - keep as-is
        });
      }

      // Update formData with new staff settings
      setFormData({
        ...formData,
        staffSettings: updatedStaffSettings,
      });
    },
    [editingService, formData, initialFormData, hasStaffCustomization],
  );

  const handleUpdateStaffModalConfirm = useCallback(
    (option: "staff_without_customization" | "all_staff") => {
      setUpdateStaffModalOpened(false);
      setSelectedUpdateOption(option);

      // Update staff settings based on selected option
      updateStaffSettingsFromOption(option);

      // Route to Additional Information step
      setInfoModalOpened(false);
      setAdditionalModalOpened(true);
      setChangedFields([]);
    },
    [updateStaffSettingsFromOption],
  );

  const handleListNext = useCallback(() => {
    if (!services || services.length === 0) {
      notifyWarning("You must add at least one service to continue.", {
        title: "Add a Service",
        icon: <IconInfoCircle size={18} />,
        withCloseButton: true,
      });
      return;
    }

    // Fire POST async (don't wait), update local state optimistically, route based on state
    // onComplete handles: optimistic state update, routing decision, and POST completion
    onComplete(updateOnboardingProgress);
  }, [services, updateOnboardingProgress, onComplete]);

  const renderIntroStep = () => (
    <Stack
      align="center"
      justify="center"
      gap="md"
      className={styles.introContainer}
    >
      <Logo variant="icon" size="lg" />
      <Text size="xl" fw={700} ta="center">
        Add Services
      </Text>
      <Stack gap="xs" ta="center" className={styles.benefitsList}>
        <Group gap="xs" justify="center">
          <IconCheck size={16} color="#868e96" />
          <Text size="sm" ta="center">
            Make it easy for clients to see and book your services.
          </Text>
        </Group>
        <Group gap="xs" justify="center">
          <IconCheck size={16} color="#868e96" />
          <Text size="sm" ta="center">
            Automate scheduling with clear pricing and durations.
          </Text>
        </Group>
        <Group gap="xs" justify="center">
          <IconCheck size={16} color="#868e96" />
          <Text size="sm" ta="center">
            Monitor service popularity and revenue over time.
          </Text>
        </Group>
      </Stack>
    </Stack>
  );

  const renderListStep = () => {
    // Group services by type
    const groupedServices: Record<string, any[]> = {};
    services?.forEach((service: any) => {
      const type = service.service_type || "Other";
      if (!groupedServices[type]) groupedServices[type] = [];
      groupedServices[type].push(service);
    });

    const hasServices = services && services.length > 0;

    return (
      <Box className={styles.listContainer}>
        <Text size="lg" fw={600} mb="xs">
          Add Services
        </Text>

        {!hasServices && (
          <Text size="sm" c="dimmed" mb="md">
            You must add at least{" "}
            <Text span fw={600}>
              one service
            </Text>{" "}
            to access your booking site.
          </Text>
        )}

        <Button
          fullWidth
          leftSection={<IconPlus size={16} />}
          onClick={() => handleAddService()}
          mb="lg"
          className={styles.addServiceButton}
          styles={{
            root: {
              backgroundColor: brandColors.teal,
              "&:hover": {
                backgroundColor: brandColors.teal,
              },
            },
          }}
        >
          Add Service
        </Button>

        {/* Suggested Services */}
        {!hasServices && showSuggestions && (
          <Box className={styles.suggestionsSection}>
            <Group
              justify="space-between"
              align="center"
              className={styles.suggestionsHeader}
            >
              <Group
                gap="sm"
                align="center"
                className={styles.suggestionsHeaderLeft}
              >
                <Box className={styles.suggestionsLightbulbContainer}>
                  <IconBulb size={18} color="#101820" />
                </Box>
                <Stack gap={4} className={styles.suggestionsTitleSubtext}>
                  <Text size="sm" fw={500}>
                    Suggested Services for Your Business
                  </Text>
                  <Text size="xs" c="dimmed">
                    Edit or add your own anytime.
                  </Text>
                </Stack>
              </Group>
              <Group
                gap="xs"
                align="center"
                className={styles.suggestionsHeaderRight}
              >
                <button
                  className={styles.carouselNavButton}
                  onClick={() =>
                    setSuggestionIndex(Math.max(0, suggestionIndex - 2))
                  }
                  disabled={suggestionIndex === 0}
                  aria-label="Previous suggestions"
                >
                  <IconChevronLeft size={16} />
                </button>
                <button
                  className={styles.carouselNavButton}
                  onClick={() =>
                    setSuggestionIndex(
                      Math.min(
                        SUGGESTED_SERVICES.length - 2,
                        suggestionIndex + 2,
                      ),
                    )
                  }
                  disabled={suggestionIndex >= SUGGESTED_SERVICES.length - 2}
                  aria-label="Next suggestions"
                >
                  <IconChevronRight size={16} />
                </button>
                <button
                  className={styles.closeButton}
                  onClick={() => setShowSuggestions(false)}
                  aria-label="Close suggestions"
                >
                  <IconX size={14} />
                </button>
              </Group>
            </Group>
            <Group gap="md" className={styles.carouselContainer}>
              {SUGGESTED_SERVICES.slice(
                suggestionIndex,
                suggestionIndex + 2,
              ).map((suggestion, index) => (
                <Box
                  key={suggestionIndex + index}
                  className={styles.suggestionCardWrapper}
                >
                  <Stack gap="xs" className={styles.suggestionCard}>
                    <Text size="sm" fw={600}>
                      {suggestion.name}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {suggestion.type} • {suggestion.duration} minutes • $
                      {suggestion.price}
                    </Text>
                    <Button
                      size="xs"
                      variant="outline"
                      mt="xs"
                      fullWidth
                      styles={{
                        root: {
                          borderColor: brandColors.teal,
                          color: brandColors.teal,
                          "&:hover": {
                            borderColor: brandColors.teal,
                            backgroundColor: "transparent",
                          },
                        },
                      }}
                      onClick={() =>
                        handleAddService({
                          name: suggestion.name,
                          serviceType: suggestion.type,
                          duration: suggestion.duration,
                          price: suggestion.price,
                        })
                      }
                    >
                      Add Service
                    </Button>
                  </Stack>
                </Box>
              ))}
            </Group>
          </Box>
        )}

        {/* Service List */}
        {hasServices &&
          Object.entries(groupedServices).map(([type, typeServices]) => (
            <Box key={type} className={styles.serviceGroup}>
              <Text size="sm" fw={600} c="dimmed" mb="xs">
                {type}
              </Text>
              {typeServices.map((service: any) => (
                <Group
                  key={service.id}
                  justify="space-between"
                  align="center"
                  className={styles.serviceCard}
                >
                  <Stack gap={4} className={styles.serviceInfo}>
                    <Text fw={600}>{service.name}</Text>
                    <Text size="sm" c="dimmed">
                      {service.service_type} • {service.default_duration_mins}{" "}
                      min • ${service.default_price}
                    </Text>
                  </Stack>
                  <Group gap="xs">
                    {deletingServiceId === service.id ? (
                      <Loader size="sm" />
                    ) : (
                      <>
                        <Button
                          variant="subtle"
                          size="xs"
                          leftSection={<IconEdit size={14} />}
                          onClick={() => handleEditService(service)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="subtle"
                          size="xs"
                          color="red"
                          leftSection={<IconTrash size={14} />}
                          onClick={() => handleDeleteService(service)}
                        >
                          Delete
                        </Button>
                      </>
                    )}
                  </Group>
                </Group>
              ))}
            </Box>
          ))}
      </Box>
    );
  };

  const labelStyles = {
    label: { fontWeight: 400, fontSize: "var(--mantine-font-size-sm)" },
  };

  const renderAddInfoStep = () => (
    <Stack gap="md" className={styles.formContainer}>
      <TextInput
        label="Service Name"
        placeholder="Service Name"
        value={formData.name}
        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        required
        styles={labelStyles}
        maxLength={256}
      />

      <TextInput
        label="Service Type"
        placeholder="Service Type"
        value={formData.serviceType}
        onChange={(e) =>
          setFormData({ ...formData, serviceType: e.target.value })
        }
        required
        styles={labelStyles}
        maxLength={128}
      />

      <ServiceDescriptionEditor
        label="Description"
        placeholder="Service Description"
        value={formData.description}
        onChange={(html) => setFormData({ ...formData, description: html })}
      />

      <Select
        label={
          <Group gap={4} align="normal">
            <span>Base Duration</span>
            <Tooltip
              label="Active time spent on service initially"
              position="right-start"
              multiline
              w={300}
            >
              <IconInfoCircle
                size={16}
                style={{
                  display: "inline-block",
                  cursor: "help",
                  verticalAlign: "middle",
                }}
              />
            </Tooltip>
          </Group>
        }
        placeholder="Select duration"
        value={String(formData.duration)}
        onChange={(value) =>
          setFormData({ ...formData, duration: Number(value) || 60 })
        }
        data={generateDurationOptions()}
        required
        styles={labelStyles}
      />

      <Select
        label={
          <Group gap={4} align="normal">
            <span>Processing Time</span>
            <Tooltip
              label="Inactive time for treatments/products to process (e.g., color development)"
              position="right-start"
              multiline
              w={300}
            >
              <IconInfoCircle
                size={16}
                style={{
                  display: "inline-block",
                  cursor: "help",
                  verticalAlign: "middle",
                }}
              />
            </Tooltip>
          </Group>
        }
        placeholder="Select processing time"
        value={String(formData.processingTime ?? "0")}
        onChange={(value) => {
          // Convert "None" (value "0") to 0
          const numValue =
            value === "0" || value === null ? 0 : Number(value) || 0;
          setFormData({ ...formData, processingTime: numValue });
        }}
        data={[{ value: "0", label: "None" }, ...generateDurationOptions()]}
        styles={labelStyles}
      />

      <Select
        label={
          <Group gap={4} align="normal">
            <span>Finishing Time</span>
            <Tooltip
              label="Active time to finalize the service after processing"
              position="right-start"
              multiline
              w={300}
            >
              <IconInfoCircle
                size={16}
                style={{
                  display: "inline-block",
                  cursor: "help",
                  verticalAlign: "middle",
                }}
              />
            </Tooltip>
          </Group>
        }
        placeholder="Select finishing time"
        value={String(formData.finishingTime ?? "0")}
        onChange={(value) => {
          // Convert "None" (value "0") to 0
          const numValue =
            value === "0" || value === null ? 0 : Number(value) || 0;
          setFormData({ ...formData, finishingTime: numValue });
        }}
        data={[{ value: "0", label: "None" }, ...generateDurationOptions()]}
        styles={labelStyles}
      />

      <Select
        label={
          <Group gap={4} align="normal">
            <span>Buffer Time</span>
            <Tooltip
              label="Time blocked before next appointment begins (ex: cleanup, breaks)"
              position="right-start"
              multiline
              w={300}
            >
              <IconInfoCircle
                size={16}
                style={{
                  display: "inline-block",
                  cursor: "help",
                  verticalAlign: "middle",
                }}
              />
            </Tooltip>
          </Group>
        }
        placeholder="Select buffer time"
        value={String(formData.bufferTime ?? "0")}
        onChange={(value) => {
          // Convert "None" (value "0") to 0
          const numValue =
            value === "0" || value === null ? 0 : Number(value) || 0;
          setFormData({ ...formData, bufferTime: numValue });
        }}
        data={[{ value: "0", label: "None" }, ...generateDurationOptions()]}
        styles={labelStyles}
      />

      <Group gap={4} align="flex-start">
        <Checkbox
          label="Enable Double Booking (Recommended)"
          checked={formData.enableDoubleBooking ?? true}
          onChange={(event) =>
            setFormData({
              ...formData,
              enableDoubleBooking: event.currentTarget.checked,
            })
          }
          styles={labelStyles}
        />
        <Tooltip
          label="When enabled, processing time can be booked for other services"
          position="right"
          multiline
          w={250}
        >
          <IconInfoCircle
            size={16}
            style={{ display: "inline-block", cursor: "help" }}
          />
        </Tooltip>
      </Group>

      {businessInfo?.id === process.env.REACT_APP_FARON_SALON_UUID && (
        <Checkbox
          label="Private Room"
          checked={formData.faron__is_private_room}
          onChange={(event) =>
            setFormData({
              ...formData,
              faron__is_private_room: event.currentTarget.checked,
            })
          }
          styles={labelStyles}
        />
      )}

      {businessInfo?.stripe_connect_account_id &&
      (businessInfo?.stripe_connect_account_is_onboarded === true ||
        businessInfo?.stripe_connect_account_is_onboarded === "true") ? (
        <Box
          style={{
            display: "grid",
            gridTemplateColumns: "3fr 1fr",
            gap: "1rem",
            alignItems: "flex-start",
          }}
        >
          <NumberInput
            label="Price"
            required
            value={formData.price}
            onChange={(value) =>
              setFormData({
                ...formData,
                price: typeof value === "number" ? value : Number(value) || 0,
              })
            }
            placeholder="Ex: $20, $30"
            min={0}
            decimalScale={2}
            fixedDecimalScale
            prefix="$"
            styles={labelStyles}
          />
          <Stack gap="xs">
            <Group gap={4} align="center" wrap="nowrap">
              <Text size="sm" fw={400}>
                Require card on file to book
              </Text>
              <Tooltip
                label="When enabled, customers must enter and save credit card details before booking this service. Requires the business to be successfully onboarded to Stripe Payments."
                position="right"
                multiline
                w={200}
              >
                <IconInfoCircle
                  size={16}
                  style={{ cursor: "help", flexShrink: 0 }}
                />
              </Tooltip>
            </Group>
            <Switch
              checked={formData.require_card_to_book}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  require_card_to_book: e.currentTarget.checked,
                })
              }
            />
          </Stack>
        </Box>
      ) : (
        <NumberInput
          label="Price"
          required
          value={formData.price}
          onChange={(value) =>
            setFormData({
              ...formData,
              price: typeof value === "number" ? value : Number(value) || 0,
            })
          }
          placeholder="Ex: $20, $30"
          min={0}
          decimalScale={2}
          fixedDecimalScale
          prefix="$"
          styles={labelStyles}
        />
      )}

      <Box
        style={{
          display: "grid",
          gridTemplateColumns: "3fr 1fr",
          gap: "1rem",
          alignItems: "flex-start",
        }}
      >
        <ColorInput
          label="Default Calendar Color"
          value={formData.calendarColor}
          onChange={(value) =>
            setFormData({ ...formData, calendarColor: value })
          }
          format="hex"
          swatches={[
            "#3174ad",
            "#ffe06e",
            "#f7a725",
            "#fa5252",
            "#e64980",
            "#be4bdb",
            "#7950f2",
            "#4c6ef5",
            "#228be6",
            "#15aabf",
            "#12b886",
            "#40c057",
          ]}
          withEyeDropper={false}
          styles={labelStyles}
        />
        <Stack gap="xs">
          <Group gap={4} align="center" wrap="nowrap">
            <Text size="sm" fw={400}>
              Show in Online Booking
            </Text>
            <Tooltip
              label="Enable to let clients book online. Disable for internal use only."
              position="right"
              multiline
              w={200}
            >
              <IconInfoCircle
                size={16}
                style={{ cursor: "help", flexShrink: 0 }}
              />
            </Tooltip>
          </Group>
          <Switch
            checked={formData.showInOnlineBooking}
            onChange={(e) =>
              setFormData({
                ...formData,
                showInOnlineBooking: e.currentTarget.checked,
              })
            }
          />
        </Stack>
      </Box>
    </Stack>
  );

  const getStaffSettings = (employeeId: string): StaffServiceSettingsData => {
    return (
      formData.staffSettings[employeeId] ||
      createDefaultStaffSettings({
        price: formData.price,
        duration: formData.duration,
        showInOnlineBooking: formData.showInOnlineBooking,
        processingTime: formData.processingTime,
        finishingTime: formData.finishingTime,
        bufferTime: formData.bufferTime,
      })
    );
  };

  const handleStaffToggle = (employeeId: string, isChecked: boolean) => {
    if (isChecked) {
      // Enable staff - create default settings and add to staffIds
      // If "staff_without_customization" was selected, use the updated values
      // Otherwise, use current formData defaults
      const newSettings = createDefaultStaffSettings({
        price: formData.price,
        duration: formData.duration,
        showInOnlineBooking: formData.showInOnlineBooking,
        processingTime: formData.processingTime,
        finishingTime: formData.finishingTime,
        bufferTime: formData.bufferTime,
      });
      setFormData({
        ...formData,
        staffIds: [...formData.staffIds, employeeId],
        staffSettings: {
          ...formData.staffSettings,
          [employeeId]: newSettings,
        },
      });
    } else {
      // Disable staff - remove from staffIds and staffSettings
      const { [employeeId]: _, ...remainingSettings } = formData.staffSettings;
      setFormData({
        ...formData,
        staffIds: formData.staffIds.filter((id) => id !== employeeId),
        staffSettings: remainingSettings,
      });
    }
  };

  const handleStaffSettingsChange = (
    employeeId: string,
    newSettings: StaffServiceSettingsData,
  ) => {
    setFormData({
      ...formData,
      staffSettings: {
        ...formData.staffSettings,
        [employeeId]: newSettings,
      },
    });
  };

  const toggleStaffAccordion = (employeeId: string) => {
    const currentSettings = getStaffSettings(employeeId);
    setFormData({
      ...formData,
      staffSettings: {
        ...formData.staffSettings,
        [employeeId]: {
          ...currentSettings,
          isOpen: !currentSettings.isOpen,
        },
      },
    });
  };

  const renderAddStaffStep = () => (
    <Stack gap="md" className={styles.formContainer}>
      <Text size="sm" c="dimmed">
        Select the employees that will be performing the service. Each employee
        can have their own pricing and duration options.
      </Text>

      {activeEmployees.length > 0 ? (
        activeEmployees.map((employee: any) => {
          const isEnabled = formData.staffIds.includes(employee.id);
          const settings = getStaffSettings(employee.id);

          return (
            <Box key={employee.id} className={styles.staffRow}>
              <Group gap="sm" align="center" className={styles.staffRowHeader}>
                <Switch
                  checked={isEnabled}
                  onChange={(e) =>
                    handleStaffToggle(employee.id, e.target.checked)
                  }
                />
                <Group
                  gap="md"
                  align="center"
                  className={styles.staffInfoWithChevron}
                  style={{ flex: 1 }}
                >
                  <Stack gap={4} className={styles.staffInfo}>
                    <Text size="sm" fw={600} className={styles.staffName}>
                      {employee.first_name} {employee.last_name}
                    </Text>
                    <Text size="sm" c="dimmed" className={styles.staffTitle}>
                      {employee.title || employee.role}
                    </Text>
                  </Stack>
                  {isEnabled && (
                    <Box
                      className={styles.accordionToggle}
                      onClick={() => toggleStaffAccordion(employee.id)}
                      style={{ cursor: "pointer" }}
                    >
                      {settings.isOpen ? (
                        <IconChevronUp size={16} />
                      ) : (
                        <IconChevronDown size={16} />
                      )}
                    </Box>
                  )}
                </Group>
              </Group>

              {/* Accordion content - only shown when enabled */}
              {isEnabled && (
                <Collapse in={settings.isOpen}>
                  <Box className={styles.accordionContent} pt="sm">
                    <StaffServiceSettingsFields
                      settings={settings}
                      onChange={(newSettings) =>
                        handleStaffSettingsChange(employee.id, newSettings)
                      }
                    />
                  </Box>
                </Collapse>
              )}
            </Box>
          );
        })
      ) : (
        <Text size="sm" c="dimmed" ta="center" py="xl">
          No employees available. You can assign staff later.
        </Text>
      )}
    </Stack>
  );

  const renderContent = () => {
    switch (currentStep) {
      case "intro":
        return renderIntroStep();
      case "list":
        return renderListStep();
      default:
        return null;
    }
  };

  const renderFooter = () => {
    switch (currentStep) {
      case "intro":
        return (
          <>
            <Button variant="subtle" color="gray" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="outline" onClick={onClose}>
              Back
            </Button>
            <Button
              onClick={handleIntroNext}
              loading={isLoading}
              styles={{
                root: {
                  backgroundColor: brandColors.teal,
                  "&:hover": {
                    backgroundColor: brandColors.teal,
                  },
                },
              }}
            >
              Next
            </Button>
          </>
        );
      case "list":
        return (
          <>
            <Button variant="subtle" color="gray" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="outline" onClick={() => setCurrentStep("intro")}>
              Back
            </Button>
            <Button
              onClick={handleListNext}
              loading={isLoading}
              styles={{
                root: {
                  backgroundColor: brandColors.teal,
                  "&:hover": {
                    backgroundColor: brandColors.teal,
                  },
                },
              }}
            >
              Next
            </Button>
          </>
        );
      default:
        return null;
    }
  };

  // Show blue dots on landing/list pages (intro = dot 1, list = dot 2)
  const shouldShowDots = currentStep === "list" || currentStep === "intro";
  const totalSubStepsForDots = shouldShowDots ? 2 : undefined;
  const currentSubStepForDots = shouldShowDots
    ? currentStep === "intro"
      ? 1
      : 2
    : undefined;

  return (
    <>
      <ProgressiveWizardShell
        opened={opened}
        onClose={onClose}
        currentStep="services"
        currentSubStep={currentSubStepForDots}
        totalSubSteps={totalSubStepsForDots}
        employeesStatus={progress.progressiveEmployees}
        servicesStatus={progress.progressiveServices}
        paymentsStatus={progress.progressivePayments}
        footer={<Group>{renderFooter()}</Group>}
        onStepClick={onSwitchWizard}
      >
        {renderContent()}
      </ProgressiveWizardShell>

      {/* Service Information Modal */}
      <Modal
        opened={infoModalOpened}
        onClose={handleInfoModalClose}
        title="Service Information"
        size="xl"
        centered
        transitionProps={{ duration: 0 }}
      >
        <Stack gap="md">
          <CustomStepper
            active={0}
            steps={[
              { label: "Service Information" },
              { label: "Additional Information" },
              { label: "Performed By" },
            ]}
          />
          {renderAddInfoStep()}
          <Group justify="flex-end" mt="md">
            <Button
              variant="subtle"
              color="gray"
              onClick={handleInfoModalClose}
            >
              Cancel
            </Button>
            <Button
              onClick={handleInfoNext}
              disabled={!!infoFieldsInvalid}
              styles={{
                root: {
                  backgroundColor: brandColors.teal,
                  "&:hover": {
                    backgroundColor: brandColors.teal,
                  },
                },
              }}
            >
              Next
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Additional Information Modal */}
      <Modal
        opened={additionalModalOpened}
        onClose={handleAdditionalModalClose}
        title="Additional Information"
        size="xl"
        centered
        transitionProps={{ duration: 0 }}
      >
        <Stack gap="md">
          <CustomStepper
            active={1}
            steps={[
              { label: "Service Information" },
              { label: "Additional Information" },
              { label: "Performed By" },
            ]}
          />

          {showRequiredForms && (
            <RequiredFormsSection
              salonServiceId={editingService?.id ?? ""}
              pendingForms={
                !editingService?.id ? pendingRequiredForms : undefined
              }
              onAddPendingForm={
                !editingService?.id
                  ? (form) => setPendingRequiredForms((prev) => [...prev, form])
                  : undefined
              }
              onRemovePendingForm={
                !editingService?.id
                  ? (tempId) =>
                      setPendingRequiredForms((prev) =>
                        prev.filter((f) => f.tempId !== tempId),
                      )
                  : undefined
              }
            />
          )}

          {showRequiredForms && (
            <ServicePreAppointmentDocumentsSection
              salonServiceId={editingService?.id ?? ""}
              readOnly={!canManageServices}
              pendingDocuments={
                !editingService?.id ? pendingPreAppointmentDocuments : undefined
              }
              onAddPendingDocument={
                !editingService?.id
                  ? (doc) =>
                      setPendingPreAppointmentDocuments((prev) => [
                        ...prev,
                        doc,
                      ])
                  : undefined
              }
              onRemovePendingDocument={
                !editingService?.id
                  ? (tempId) =>
                      setPendingPreAppointmentDocuments((prev) =>
                        prev.filter((d) => d.tempId !== tempId),
                      )
                  : undefined
              }
            />
          )}

          {businessInfo?.id && (
            <ServiceMessageSettings
              salonServiceUuid={editingService?.id ?? ""}
              readOnly={!canManageServices}
              pendingSettings={
                !editingService?.id ? pendingMessageSettings : undefined
              }
              onPendingChange={
                !editingService?.id ? setPendingMessageSettings : undefined
              }
            />
          )}

          {canViewClientForms && businessInfo?.id && (
            <ServicePostAppointmentSection
              salonServiceId={editingService?.id ?? ""}
              readOnly={!canManageServices}
              pendingSettings={
                !editingService?.id ? pendingPostAppointmentSettings : undefined
              }
              pendingMessages={
                !editingService?.id ? pendingPostAppointmentMessages : undefined
              }
              onPendingSettingsChange={
                !editingService?.id
                  ? setPendingPostAppointmentSettings
                  : undefined
              }
              onAddPendingMessage={
                !editingService?.id
                  ? (message) =>
                      setPendingPostAppointmentMessages((prev) => [
                        ...prev,
                        message,
                      ])
                  : undefined
              }
              onUpdatePendingMessage={
                !editingService?.id
                  ? (tempId, message) =>
                      setPendingPostAppointmentMessages((prev) =>
                        prev.map((p) => (p.tempId === tempId ? message : p)),
                      )
                  : undefined
              }
              onRemovePendingMessage={
                !editingService?.id
                  ? (tempId) =>
                      setPendingPostAppointmentMessages((prev) =>
                        prev.filter((p) => p.tempId !== tempId),
                      )
                  : undefined
              }
            />
          )}

          <Stack gap="xs">
            <Text fw={600} size="sm">
              Tax Rates
            </Text>
            <TaxRateMultiSelect
              value={selectedTaxRateIds}
              onChange={setSelectedTaxRateIds}
              disabled={isLoading}
            />
          </Stack>

          <Group justify="flex-end" mt="md">
            <Button variant="outline" onClick={handleAdditionalBack}>
              Back
            </Button>
            <Button
              onClick={handleAdditionalNext}
              styles={{
                root: {
                  backgroundColor: brandColors.teal,
                  "&:hover": {
                    backgroundColor: brandColors.teal,
                  },
                },
              }}
            >
              Next
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Performed By Modal */}
      <Modal
        opened={staffModalOpened}
        onClose={handleStaffModalClose}
        title="Performed By"
        size="xl"
        centered
        transitionProps={{ duration: 0 }}
      >
        <Stack gap="md">
          <CustomStepper
            active={2}
            steps={[
              { label: "Service Information" },
              { label: "Additional Information" },
              { label: "Performed By" },
            ]}
          />
          {renderAddStaffStep()}
          <Group justify="flex-end" mt="md">
            <Button variant="outline" onClick={handleStaffBack}>
              Back
            </Button>
            <Button
              onClick={handleSaveService}
              loading={isLoading}
              styles={{
                root: {
                  backgroundColor: brandColors.teal,
                  "&:hover": {
                    backgroundColor: brandColors.teal,
                  },
                },
              }}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>

      <UpdateStaffServicesModal
        opened={updateStaffModalOpened}
        onClose={() => {
          setUpdateStaffModalOpened(false);
          setPendingSaveAction(null);
          setChangedFields([]);
          setSelectedUpdateOption(null);
        }}
        onConfirm={handleUpdateStaffModalConfirm}
        changedFields={changedFields}
      />
    </>
  );
}
